import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Password } from "@vedaunion/shared";
import { EditorialAuthShell } from "../components/auth/EditorialAuthShell";
import { AuthInput } from "../components/auth/AuthInput";
import { useResetPassword } from "../lib/auth";

// Local form schema with confirm-password match. Server enforces token shape + length.
const FormSchema = z
  .object({
    newPassword: Password,
    confirm: z.string().min(1, "Please re-enter the password."),
  })
  .refine((d) => d.newPassword === d.confirm, {
    path: ["confirm"],
    message: "Passwords do not match.",
  });
type FormValues = z.infer<typeof FormSchema>;

export default function ResetPasswordPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get("token") ?? "";
  const { mutateAsync, isPending } = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { newPassword: "", confirm: "" },
  });

  // If no token, send them to forgot-password.
  useEffect(() => {
    if (!token) navigate("/forgot-password", { replace: true });
  }, [token, navigate]);

  const onSubmit = async (values: FormValues) => {
    try {
      await mutateAsync({ token, newPassword: values.newPassword });
      navigate("/login?reset=success", { replace: true });
    } catch (err) {
      const e = err as { body?: { error?: string; message?: string }; message?: string };
      const message = e?.body?.message ?? e?.message ?? "Reset failed.";
      if (e?.body?.error === "invalid-token") {
        setError("root", { type: "server", message });
      } else if (e?.body?.error === "invalid-password") {
        setError("newPassword", { type: "server", message });
      } else {
        setError("root", { type: "server", message });
      }
    }
  };

  return (
    <EditorialAuthShell
      eyebrow="Set a new password"
      headline={
        <>
          A new{" "}
          <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
            opening
          </span>
          .
        </>
      }
      lede="At least 12 characters. A short phrase works well."
      verse={{
        deva: "ॐ शान्तिः शान्तिः शान्तिः",
        iast: "Oṁ śāntiḥ śāntiḥ śāntiḥ",
        english: "Peace, peace, peace.",
        source: "Closing invocation",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <AuthInput
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          {...register("newPassword")}
          error={errors.newPassword?.message}
        />
        <AuthInput
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          {...register("confirm")}
          error={errors.confirm?.message}
        />

        <div className="auth-actions">
          <button type="submit" className="btn-primary auth-submit" disabled={isPending}>
            <span>{isPending ? "Saving…" : "Set new password"}</span>
            <span className="btn-arrow-icon" aria-hidden>
              →
            </span>
          </button>
        </div>

        {errors.root && (
          <div className="mt-3">
            <p className="auth-error">— {errors.root.message}</p>
            <div className="auth-links">
              <Link to="/forgot-password">Request a new link →</Link>
            </div>
          </div>
        )}
      </form>
    </EditorialAuthShell>
  );
}
